
// ConfigSync — a debounced trigger for the platform's Configuration Sync job.
//
// Configuration Sync is the step that PUBLISHES a project's/customer's configuration (label types + label
// values, observation types, …) out to Fieldbook and the field apps. Without it, a newly-created label exists
// in the API and shows in the config/Projects app, but never reaches Fieldbook on observations — which is
// exactly the reported gap ("we used to run a config sync on a project for the labels to show"). This wires
// that sync to run automatically after a label change.
//
// It mirrors the legacy app's manual "Config Sync" page (web/app_headlight/src/pages/ConfigurationSync.vue):
//   GET /1.0/ConfigurationSync/Sync/{Context}/{ContextID}   → queues an async job (Context ∈ Customer|Project).
//
// The endpoint is SUPER-USER-ONLY (server EndpointBase._isSuperUser: role index 5 AND customer 1). For anyone
// else it 403s; we surface that as a gentle, actionable notice rather than an error (the label change itself
// still saved — a super-user just needs to run the sync). Rapid edits are coalesced into ONE job per
// (Context, ID) by a short debounce so, e.g., seeding a default set + adding a type triggers a single sync.

const DEBOUNCE_MS = 2000;

/** @type {Record<string, any>} - pending debounce timers keyed by `${Context}:${ContextID}`. */
const _timers = {};

function _restClient(pPict)
{
	return (pPict && pPict.fable && pPict.fable.HeadlightRestClient) || (pPict && pPict.HeadlightRestClient) || null;
}

function _toast(pPict, pMessage, pType)
{
	const tmpModal = pPict && pPict.views && pPict.views['Pict-Section-Modal'];
	if (tmpModal && (typeof tmpModal.toast === 'function')) { tmpModal.toast(pMessage, { type: pType || 'info' }); }
}

function _fire(pPict, pContext, pContextID)
{
	const tmpRestClient = _restClient(pPict);
	if (!tmpRestClient) { return; }
	tmpRestClient.getJSON(`ConfigurationSync/Sync/${pContext}/${pContextID}`, (pError) =>
	{
		if (pError)
		{
			const tmpMessage = String((pError && pError.message) || pError || '');
			if (/forbidden|permission/i.test(tmpMessage))
			{
				// Not a super-user: the save succeeded, but the sync needs one. Inform, don't alarm.
				_toast(pPict, 'Saved — but a super-user Configuration Sync is needed to push this to the field apps.', 'info');
			}
			else
			{
				if (pPict && pPict.log) { pPict.log.warn(`ConfigSync: sync ${pContext}/${pContextID} failed: ${tmpMessage}`); }
				_toast(pPict, 'Couldn’t start the configuration sync automatically — a super-user can run it from Config Sync.', 'error');
			}
			return;
		}
		_toast(pPict, `Configuration sync started — the ${pContext === 'Project' ? 'project’s' : 'customer’s'} labels will reach the field apps shortly.`, 'info');
	});
}

/**
 * Request a (debounced) Configuration Sync for a context. Coalesces rapid changes into one job per (Context, ID).
 * @param {any} pPict - the pict instance (for the REST client + toasts).
 * @param {'Project'|'Customer'} pContext
 * @param {Number} pContextID
 */
function requestSync(pPict, pContext, pContextID)
{
	if (!pPict || ((pContext !== 'Project') && (pContext !== 'Customer')) || !(Number(pContextID) > 0)) { return; }
	const tmpKey = `${pContext}:${pContextID}`;
	if (_timers[tmpKey]) { clearTimeout(_timers[tmpKey]); }
	_timers[tmpKey] = setTimeout(() =>
	{
		delete _timers[tmpKey];
		_fire(pPict, pContext, pContextID);
	}, DEBOUNCE_MS);
}

// ── Scoped sync (the new, side-by-side endpoint) ─────────────────────────────────
//
// requestScopedSync targets the SCOPE-AWARE reimplementation:
//   GET /1.0/ConfigurationSync/SyncScoped/{Context}/{ContextID}[?Manifest=<Name>]   (Context ∈ Customer|Project|Global)
// It rebuilds only the requested level(s) — not the whole platform — and, with a Manifest name, only that one
// ObservationType's per-type config. Unlike the legacy /Sync route this endpoint also admits a customer's own
// config-admin (tenancy + Module-PlatformConfiguration-Access checked server-side), so a 403 here means "not
// permitted for this context", NOT "super-user required". Global is super-user-only (enforced server-side).

function _fireScoped(pPict, pContext, pContextID, pManifest)
{
	const tmpRestClient = _restClient(pPict);
	if (!tmpRestClient) { return; }
	let tmpURI = `ConfigurationSync/SyncScoped/${pContext}/${pContextID}`;
	if (pManifest) { tmpURI += `?Manifest=${encodeURIComponent(pManifest)}`; }
	tmpRestClient.getJSON(tmpURI, (pError) =>
	{
		if (pError)
		{
			const tmpMessage = String((pError && pError.message) || pError || '');
			if (/forbidden|permission/i.test(tmpMessage))
			{
				_toast(pPict, 'You don’t have permission to run a configuration sync for this context.', 'info');
			}
			else
			{
				if (pPict && pPict.log) { pPict.log.warn(`ConfigSync: scoped sync ${pContext}/${pContextID} failed: ${tmpMessage}`); }
				_toast(pPict, 'Couldn’t start the configuration sync — please try again or contact a Headlight admin.', 'error');
			}
			return;
		}
		const tmpWhat = (pContext === 'Project') ? 'project’s' : (pContext === 'Global' ? 'platform' : 'customer’s');
		_toast(pPict, `Configuration sync started — the ${tmpWhat} configuration will reach the field apps shortly.`, 'info');
	});
}

/**
 * Request a scoped Configuration Sync via the new endpoint. Coalesces rapid changes into one job per
 * (Context, ID, Manifest) unless pOptions.immediate is set (used by the manual dashboard buttons).
 * @param {Object} pPict - the pict instance (for the REST client + toasts).
 * @param {String} pContext - 'Customer', 'Project' or 'Global'.
 * @param {Number} pContextID - the customer/project id (ignored for Global).
 * @param {Object} [pOptions] - { manifest?: String (limit to one ObservationType), immediate?: Boolean (skip debounce) }.
 * @return {void}
 */
function requestScopedSync(pPict, pContext, pContextID, pOptions)
{
	const tmpOptions = pOptions || {};
	if (!pPict || ((pContext !== 'Customer') && (pContext !== 'Project') && (pContext !== 'Global'))) { return; }
	if ((pContext !== 'Global') && !(Number(pContextID) > 0)) { return; }
	const tmpID = (pContext === 'Global') ? 0 : Number(pContextID);
	const tmpManifest = tmpOptions.manifest || null;
	if (tmpOptions.immediate)
	{
		_fireScoped(pPict, pContext, tmpID, tmpManifest);
		return;
	}
	const tmpKey = `scoped:${pContext}:${tmpID}:${tmpManifest || ''}`;
	if (_timers[tmpKey]) { clearTimeout(_timers[tmpKey]); }
	_timers[tmpKey] = setTimeout(() =>
	{
		delete _timers[tmpKey];
		_fireScoped(pPict, pContext, tmpID, tmpManifest);
	}, DEBOUNCE_MS);
}

module.exports = { requestSync, requestScopedSync };
